import {
  collection,
  deleteField,
  getDocs,
  query,
  where,
  Timestamp,
  type DocumentData,
  type Firestore,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';

interface CachedRecord<T> {
  data: T;
  updatedAt: string;
}

interface CacheEnvelope<T> {
  records: Record<string, CachedRecord<T>>;
  lastSyncAt: string;
  /** Última reconciliação completa (não incremental) — ver `syncCollectionToCache`. */
  lastFullSyncAt?: string;
}

// Cooldown pra reconciliação completa — mesmo padrão de 15-30min já usado no
// sync do app Android (ver conectar.md). A query incremental (`updatedAt >`)
// nunca detecta um doc que foi excluído DE VERDADE (deleteDoc, ex. direto no
// console do Firebase) — ele só desaparece da consulta, não retorna com
// `deleted: true` pro cache saber remover. Isso não acontece pelo fluxo normal
// do site (deleteEvent/deleteAnalysis só fazem soft-delete), mas uma
// reconciliação periódica com leitura cheia da coleção detecta e limpa esse
// caso sem custar uma leitura cheia a cada visita.
const RECONCILE_COOLDOWN_MS = 20 * 60_000;

function readEnvelope<T>(cacheKey: string): CacheEnvelope<T> {
  try {
    const raw = localStorage.getItem(cacheKey);
    if (raw) return JSON.parse(raw) as CacheEnvelope<T>;
  } catch {
    /* cache corrompido — refaz do zero a partir do Firestore */
  }
  return { records: {}, lastSyncAt: '' };
}

function writeEnvelope<T>(cacheKey: string, envelope: CacheEnvelope<T>) {
  try {
    localStorage.setItem(cacheKey, JSON.stringify(envelope));
  } catch (err) {
    // Cache local é só uma otimização de leitura — se a foto em base64 do
    // evento estourar a cota do localStorage, seguimos sem cache em vez de
    // quebrar a criação/edição (os dados continuam corretos no Firestore).
    console.warn(`Falha ao gravar cache local (${cacheKey}), continuando sem cache:`, err);
  }
}

function itemsOf<T>(envelope: CacheEnvelope<T>): T[] {
  return Object.values(envelope.records).map((r) => r.data);
}

/** Firestore rejeita campos com valor `undefined` — remove antes de setDoc/updateDoc. */
export function stripUndefined<T extends object>(obj: T): Partial<T> {
  const result: Partial<T> = {};
  for (const key of Object.keys(obj) as (keyof T)[]) {
    if (obj[key] !== undefined) result[key] = obj[key];
  }
  return result;
}

/**
 * Para updateDoc: campos `undefined` viram deleteField() em vez de serem
 * omitidos. Omitir mantém o valor antigo no Firestore (updateDoc só altera
 * as chaves presentes) — quando o patch representa "limpar este campo"
 * (ex.: desmarcar "Outro" tipo), isso deixava um valor fantasma salvo.
 */
export function stripUndefinedForUpdate<T extends object>(obj: T): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(obj) as (keyof T)[]) {
    result[key as string] = obj[key] === undefined ? deleteField() : obj[key];
  }
  return result;
}

export function cacheKeyFor(collectionName: string, uid: string) {
  return `conectar-cache:${collectionName}:${uid}`;
}

export function loadCachedItems<T>(cacheKey: string): T[] {
  return itemsOf(readEnvelope<T>(cacheKey));
}

export function upsertCachedItem<T>(cacheKey: string, id: string, data: T) {
  const envelope = readEnvelope<T>(cacheKey);
  envelope.records[id] = { data, updatedAt: new Date().toISOString() };
  writeEnvelope(cacheKey, envelope);
}

export function removeCachedItem<T>(cacheKey: string, id: string) {
  const envelope = readEnvelope<T>(cacheKey);
  delete envelope.records[id];
  writeEnvelope(cacheKey, envelope);
}

/**
 * Busca no Firestore — coleção compartilhada por toda a equipe — apenas
 * os documentos alterados depois do último `lastSyncAt` salvo em cache
 * (where updatedAt > lastSyncAt). Se o cache já está em dia, a consulta
 * não traz nenhum documento — é assim que evitamos leituras repetidas a
 * cada visita ao site.
 *
 * Reconciliação: fora do cooldown de `RECONCILE_COOLDOWN_MS`, troca a query
 * incremental por uma leitura cheia da coleção e remove do cache qualquer id
 * que não veio nessa leitura — cobre o caso de exclusão feita fora do site
 * (deleteDoc direto no console do Firebase), que o `deleted: true` do
 * soft-delete normal não cobre porque o doc simplesmente deixa de existir.
 */
export async function syncCollectionToCache<T>(opts: {
  db: Firestore;
  cacheKey: string;
  collectionName: string;
  parseDoc: (snap: QueryDocumentSnapshot<DocumentData>) => {
    id: string;
    data: T;
    updatedAt: string;
    deleted?: boolean;
  };
}): Promise<T[]> {
  const { db, cacheKey, collectionName, parseDoc } = opts;
  const envelope = readEnvelope<T>(cacheKey);
  const hasCache = Boolean(envelope.lastSyncAt);
  const dueForReconcile =
    !hasCache ||
    !envelope.lastFullSyncAt ||
    Date.now() - new Date(envelope.lastFullSyncAt).getTime() > RECONCILE_COOLDOWN_MS;

  const snap = await getDocs(
    dueForReconcile
      ? collection(db, collectionName)
      : query(
          collection(db, collectionName),
          where('updatedAt', '>', Timestamp.fromDate(new Date(envelope.lastSyncAt)))
        )
  );

  const seenIds = new Set<string>();
  for (const docSnap of snap.docs) {
    const { id, data, updatedAt, deleted } = parseDoc(docSnap);
    seenIds.add(id);
    if (deleted) {
      delete envelope.records[id];
    } else {
      envelope.records[id] = { data, updatedAt };
    }
  }

  if (dueForReconcile) {
    for (const id of Object.keys(envelope.records)) {
      if (!seenIds.has(id)) delete envelope.records[id];
    }
    envelope.lastFullSyncAt = new Date().toISOString();
  }

  envelope.lastSyncAt = new Date().toISOString();
  writeEnvelope(cacheKey, envelope);
  return itemsOf(envelope);
}
